import * as React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  InputLabel,
  OutlinedInput,
  LinearProgress,
  Button,
  Stack,
  Chip,
  FormControlLabel,
  Checkbox,
  Typography,
  Paper,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import Layout from "../../components/layout";
import ValidationModal from "../BootstrapValidationModal";
import salonService from "../../services/salonServices";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DecryptData, EncryptData, jsonParser } from "../../helpers/helper";
import { toast } from "react-toastify";
import dayjs from "dayjs";

function Deals() {
  const history = useHistory();

  // Loading & Submission State
  const [progress, setProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingModalOpen, setIsSubmittingModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  // Form Data State
  const [serviceName, setServiceName] = useState("");
  const [price, setPrice] = useState<string>("");
  const [description, setDescription] = useState("");
  const [serviceInput, setServiceInput] = useState("");
  const [services, setServices] = useState<string[]>([]);

  // Date State
  const [startDate, setStartDate] = useState<any>(dayjs());
  const [endDate, setEndDate] = useState<any>(null);
  const [isPermanent, setIsPermanent] = useState(false);

  // Validation State
  const [errors, setErrors] = useState({
    serviceName: false,
    price: false,
    services: false,
    startDate: false,
    endDate: false,
  });

  const [serviceTypeData, setServiceTypeData] = useState<any[]>([]);
  const [userId, setUserId] = useState<number>();

  /* ---------------- INITIAL FETCH ---------------- */
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const data = await salonService.findAll();
        const decryptData = await DecryptData(data.serviceType);
        const parsedData = jsonParser(decryptData);

        // Safely map names
        const mapped = await Promise.all(
          parsedData.map(async (d: any) => ({
            ...d,
            serviceName: await DecryptData(d.serviceName),
          }))
        );

        setServiceTypeData(mapped);
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        setUserId(user.id);
      } catch (err) {
        console.error("Initialization error", err);
      }
    };
    fetchInitialData();
  }, []);

  /* ---------------- PROGRESS BAR LOGIC ---------------- */
  useEffect(() => {
    if (!isSubmittingModalOpen) return;
    let value = 0;
    const interval = setInterval(() => {
      value += 5;
      setProgress(value);
      if (value >= 100) {
        clearInterval(interval);
        history.push("/services");
      }
    }, 30);
    return () => clearInterval(interval);
  }, [isSubmittingModalOpen, history]);

  /* ---------------- HANDLERS ---------------- */
  const handleAddService = () => {
    const val = serviceInput.trim();
    if (!val) return;
    if (!services.includes(val)) {
      setServices([...services, val]);
      setErrors((prev) => ({ ...prev, services: false }));
    }
    setServiceInput("");
  };

  const handleRemoveService = (index: number) => {
    setServices(services.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {
      serviceName: !serviceName.trim(),
      price: !price || Number(price) <= 0,
      services: services.length === 0,
      startDate: !startDate,
      endDate:
        !isPermanent &&
        (!endDate || (endDate && startDate && endDate.isBefore(startDate))),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((v) => v === true);
  };

  const handleSubmit = async () => {
    if (!validate()) {
      setIsErrorModalOpen(true);
      if (errors.endDate) toast.error("Please check the End Date logic");
      return;
    }

    setIsSubmitting(true);
    try {
      const dealCategory = serviceTypeData.find(
        (s) => s.serviceName === "Deals"
      );

      const payload = {
        serviceName: EncryptData(serviceName),
        price: EncryptData(price),
        description: EncryptData(description),
        serviceTypeId: dealCategory?.id,
        startDate: startDate.toISOString(),
        endDate: isPermanent ? null : endDate.toISOString(),
        userId,
        services: services.map((s) => EncryptData(s)),
        deal: true,
      };

      await salonService.save(payload);
      setIsSubmittingModalOpen(true);
    } catch (err) {
      toast.error("Failed to save deal. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <Layout>
      <Box sx={{ maxWidth: 1000, mx: "auto", p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            bgcolor: "#fff",
            border: "1px solid #eee",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, mb: 3, color: "#2d3436" }}
          >
            Create New Service Deal
          </Typography>

          <Grid container spacing={3}>
            {/* Deal Name */}
            <Grid item xs={12} md={6}>
              <InputLabel sx={{ fontWeight: 700, mb: 1, fontSize: "0.85rem" }}>
                DEAL TITLE
              </InputLabel>
              <OutlinedInput
                fullWidth
                value={serviceName}
                error={errors.serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                placeholder="e.g. Ramadan Special Combo"
                size="small"
              />
            </Grid>

            {/* Price */}
            <Grid item xs={12} md={6}>
              <InputLabel sx={{ fontWeight: 700, mb: 1, fontSize: "0.85rem" }}>
                DEAL PRICE (RS)
              </InputLabel>
              <OutlinedInput
                fullWidth
                value={price}
                error={errors.price}
                onChange={(e) =>
                  setPrice(e.target.value.replace(/[^0-9]/g, ""))
                }
                placeholder="0.00"
                size="small"
              />
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} md={6}>
              <InputLabel sx={{ fontWeight: 700, mb: 1, fontSize: "0.85rem" }}>
                VALID FROM
              </InputLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={startDate}
                  onChange={(val) => setStartDate(val)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: errors.startDate,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* End Date */}
            <Grid item xs={12} md={6}>
              {/* Wrap label and checkbox in a flex row */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 0.5, height: "24px" }} // Fixed height prevents pushing the input
              >
                <InputLabel
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    color: "#2d3436",
                  }}
                >
                  VALID UNTIL
                </InputLabel>

                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isPermanent}
                      onChange={(e) => {
                        setIsPermanent(e.target.checked);
                        if (e.target.checked) setEndDate(null);
                      }}
                      sx={{
                        p: 0, // Remove padding to keep it tight
                        color: "#719879",
                        "&.Mui-checked": { color: "#719879" },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{ fontSize: "0.75rem", fontWeight: 600, ml: 0.5 }}
                    >
                      Never Ends
                    </Typography>
                  }
                  sx={{ mr: 0 }} // Remove right margin
                />
              </Box>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={isPermanent ? null : endDate}
                  disabled={isPermanent}
                  onChange={(val) => setEndDate(val)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !isPermanent && errors.endDate,
                      placeholder: isPermanent ? "Ongoing" : "Select Date",
                      sx: {
                        // Optional: Dim the input when disabled to look cleaner
                        "& .MuiInputBase-root.Mui-disabled": {
                          backgroundColor: "#f9f9f9",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Services List (Tags) */}
            <Grid item xs={12}>
              <InputLabel sx={{ fontWeight: 700, mb: 1, fontSize: "0.85rem" }}>
                INCLUDED SERVICES
              </InputLabel>
              <OutlinedInput
                fullWidth
                value={serviceInput}
                error={errors.services}
                onChange={(e) => setServiceInput(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && (e.preventDefault(), handleAddService())
                }
                placeholder="Type service name and press Enter..."
                size="small"
                endAdornment={
                  <Button onClick={handleAddService} sx={{ color: "#719879" }}>
                    Add
                  </Button>
                }
              />
              <Stack
                direction="row"
                spacing={1}
                mt={2}
                flexWrap="wrap"
                useFlexGap
              >
                {services.map((s, i) => (
                  <Chip
                    key={i}
                    label={s}
                    onDelete={() => handleRemoveService(i)}
                    sx={{
                      bgcolor: "#f1f8f2",
                      color: "#719879",
                      fontWeight: 600,
                      border: "1px solid #719879",
                    }}
                  />
                ))}
              </Stack>
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <InputLabel sx={{ fontWeight: 700, mb: 1, fontSize: "0.85rem" }}>
                DESCRIPTION / TERMS
              </InputLabel>
              <OutlinedInput
                fullWidth
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional details about this deal..."
              />
            </Grid>

            {/* Actions */}
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="flex-end"
              gap={2}
              mt={2}
            >
              <Button
                variant="text"
                color="inherit"
                onClick={() => history.push("/services")}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={isSubmitting}
                onClick={handleSubmit}
                sx={{
                  px: 6,
                  py: 1.2,
                  borderRadius: "10px",
                  background: "#719879",
                  boxShadow: "0 4px 12px rgba(113, 152, 121, 0.3)",
                  "&:hover": { background: "#5f8267" },
                }}
              >
                {isSubmitting ? "Processing..." : "Create Deal"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* MODALS */}
      <ValidationModal isOpen={isSubmittingModalOpen} onClose={() => {}}>
        <Box p={3} textAlign="center">
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: 5,
              mb: 2,
              bgcolor: "#eee",
              "& .MuiLinearProgress-bar": { bgcolor: "#719879" },
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Saving your deal and redirecting...
          </Typography>
        </Box>
      </ValidationModal>

      <ValidationModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
      >
        <Box p={4} textAlign="center">
          <Typography variant="h6" color="error" gutterBottom>
            Missing Information
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Please ensure all fields marked in red are filled correctly.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setIsErrorModalOpen(false)}
            sx={{ bgcolor: "#2d3436" }}
          >
            Understood
          </Button>
        </Box>
      </ValidationModal>
    </Layout>
  );
}

export default Deals;
